import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/** Ensures radio groups stay unique when several selectors share a page. */
let instanceCount = 0;

/**
 * Maps a money format's amount placeholder to its no-decimals equivalent, so
 * whole-dollar display still honours the shop's own separator conventions.
 * @type {Record<string, string>}
 */
const NO_DECIMAL_PLACEHOLDERS = {
  amount: 'amount_no_decimals',
  amount_with_comma_separator: 'amount_no_decimals_with_comma_separator',
  amount_with_space_separator: 'amount_no_decimals_with_space_separator',
};

/**
 * Formats a money value, optionally dropping the cents.
 *
 * formatMoney's no-decimals path rounds, so $59.99 would read as $60. Flooring
 * to whole dollars first keeps it at $59 and matches the Liquid render, which
 * relies on integer `divided_by` doing the same.
 *
 * @param {number} cents
 * @param {string} format - The shop's money format.
 * @param {string} currency
 * @param {boolean} hideCents
 * @returns {string}
 */
function formatTierPrice(cents, format, currency, hideCents) {
  if (!hideCents) return formatMoney(cents, format, currency);

  const wholeDollars = Math.floor(cents / 100) * 100;
  const noDecimalFormat = format.replace(
    /{{\s*(\w+)\s*}}/g,
    (match, placeholder) => (NO_DECIMAL_PLACEHOLDERS[placeholder] ? `{{${NO_DECIMAL_PLACEHOLDERS[placeholder]}}}` : match)
  );

  return formatMoney(wholeDollars, noDecimalFormat, currency);
}

/**
 * Drives the Quantity Tier Selector block (blocks/quantity-tier-selector.liquid).
 *
 * This component never touches the cart itself. It only ever writes the
 * selected tier's quantity onto the *existing* product form, then lets that
 * form's own add-to-cart / cart-drawer logic (product-form.js) run
 * unmodified - so cart AJAX, accelerated checkout, and cart-drawer rendering
 * all stay exactly as the theme already implements them.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement[]} tierInputs
 * @extends {Component<Refs>}
 */
export class QuantityTierSelectorComponent extends Component {
  requiredRefs = ['tierInputs'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });

    instanceCount += 1;
    const groupName = `quantity-tier-group-${instanceCount}`;
    for (const input of this.refs.tierInputs ?? []) {
      input.name = groupName;
    }

    this.#ensureDefaultSelection();
    this.#syncSteppers();

    // The product form's quantity-selector-component may not be upgraded yet
    // (its module script also loads async) - wait for it before syncing the
    // initial tier quantity, otherwise setValue() would silently no-op.
    Promise.all([
      customElements.whenDefined('product-form-component'),
      customElements.whenDefined('quantity-selector-component'),
    ]).then(() => this.#applySelectedTier());

    this.#broadcastSelectedPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Fires on `on:change` from any tier radio input.
   */
  handleTierChange() {
    // Any tier the shopper stepped up then navigated away from goes back to
    // its configured base quantity, so re-selecting it is never surprising.
    for (const input of this.refs.tierInputs ?? []) {
      if (!input.checked) input.dataset.currentQuantity = input.dataset.quantity ?? '1';
    }

    this.#syncSteppers();
    this.#refreshDisplayedPrices();
    this.#applySelectedTier();
    this.#broadcastSelectedPrice();
  }

  /**
   * Fires on `on:click` from a stepper +/- button.
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  stepQuantity(event) {
    // The stepper lives inside the card's <label>, so a click would otherwise
    // re-activate the radio. Suppress that default before anything else.
    event.preventDefault();
    event.stopPropagation();

    const button = event.target.closest('.quantity-tier__stepper-button');
    const card = event.target.closest('.quantity-tier__card');
    const input = /** @type {HTMLInputElement | null} */ (card?.querySelector('.quantity-tier__radio-input'));
    if (!button || !card || !input) return;

    const step = Number(button.getAttribute('data-step')) || 0;
    const min = Number(input.dataset.quantity) || 1;
    const current = this.#getTierQuantity(input);
    const next = Math.max(min, current + step);

    if (next === current) return;

    input.dataset.currentQuantity = String(next);

    this.#syncSteppers();
    this.#refreshDisplayedPrices();
    this.#applySelectedTier();
    this.#broadcastSelectedPrice();
  }

  /**
   * The quantity a tier will actually add to the cart right now - its base
   * quantity, or whatever the shopper stepped it up to.
   * @param {HTMLInputElement} input
   * @returns {number}
   */
  #getTierQuantity(input) {
    return Number(input.dataset.currentQuantity) || Number(input.dataset.quantity) || 1;
  }

  /**
   * Progressive enhancement fallback: if no tier ended up checked, or a
   * merchant enabled "Selected by default" on more than one, settle on a
   * single valid selection so the UI and the product form agree.
   */
  #ensureDefaultSelection() {
    const inputs = this.refs.tierInputs;
    if (!inputs?.length) return;

    const checked = inputs.filter((input) => input.checked);

    if (checked.length === 0) {
      inputs[0].checked = true;
      return;
    }

    // Last one wins, matching what the schema tells the merchant.
    for (const input of checked.slice(0, -1)) input.checked = false;
  }

  /**
   * Writes each stepper's displayed value and disables "-" at the tier's
   * configured minimum.
   */
  #syncSteppers() {
    for (const input of this.refs.tierInputs ?? []) {
      const card = input.closest('.quantity-tier__card');
      if (!card) continue;

      const quantity = this.#getTierQuantity(input);
      const min = Number(input.dataset.quantity) || 1;

      const valueEl = card.querySelector('.quantity-tier__stepper-value');
      if (valueEl) valueEl.textContent = String(quantity);

      const minusButton = /** @type {HTMLButtonElement | null} */ (
        card.querySelector('.quantity-tier__stepper-button[data-step="-1"]')
      );
      if (minusButton) minusButton.disabled = quantity <= min;
    }
  }

  /**
   * @returns {HTMLInputElement | undefined}
   */
  #getSelectedInput() {
    return this.refs.tierInputs?.find((input) => input.checked);
  }

  /**
   * Finds the real product form for this block's product. The block can sit
   * anywhere on the page, so the form is located by matching product id
   * rather than via closest().
   * @returns {any | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    return document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * Writes the selected tier's quantity onto the real product form, so
   * Add to cart / Buy now submit the correct amount.
   *
   * Prefers the theme's native quantity-selector-component when the
   * merchant has that block enabled. Some merchants disable it (e.g. when a
   * quantity-tier app was previously handling quantity), in which case
   * there is no [name="quantity"] input in the form at all - Shopify's
   * cart/add would then silently default to 1. To stay correct in that
   * case too, a hidden quantity input is created on the form on demand.
   */
  #applySelectedTier() {
    const selected = this.#getSelectedInput();
    if (!selected) return;

    const quantity = this.#getTierQuantity(selected);
    const productForm = this.#getProductForm();
    const form = productForm?.querySelector('form');
    if (!form) return;

    const quantitySelector = productForm.querySelector('quantity-selector-component');

    if (quantitySelector?.setValue) {
      quantitySelector.setValue(String(quantity));
      quantitySelector.updateButtonStates?.();
      // Dispatches QuantitySelectorUpdateEvent so anything else that depends
      // on quantity (e.g. price-per-item / volume pricing) stays in sync too.
      quantitySelector.onQuantityChange?.();
      return;
    }

    let hiddenQuantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    if (!hiddenQuantityInput) {
      hiddenQuantityInput = document.createElement('input');
      hiddenQuantityInput.type = 'hidden';
      hiddenQuantityInput.name = 'quantity';
      form.append(hiddenQuantityInput);
    }
    hiddenQuantityInput.value = String(quantity);
  }

  /**
   * When the shopper changes variant (size/colour), re-apply the currently
   * selected tier's quantity to the (possibly re-rendered) quantity
   * selector, and refresh the live price math for every tier card using the
   * new variant's price.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const productId = this.dataset.productId;
    if (!productId || event.detail?.data?.productId !== productId) return;

    const resource = event.detail.resource;
    if (resource?.price != null) {
      this.dataset.variantPrice = String(resource.price);
      // Variants can be on sale independently, so this has to travel with the
      // price. Absent means "no sale" rather than "keep the previous value".
      this.dataset.variantComparePrice = String(resource.compare_at_price ?? 0);
      this.#refreshDisplayedPrices();
    }

    // Defer until after the product form's own morphing has settled.
    requestAnimationFrame(() => {
      this.#applySelectedTier();
      this.#broadcastSelectedPrice();
    });
  };

  /**
   * Announces the selected tier's displayed price so other blocks (e.g. the
   * product title/price header) can mirror it. Deliberately publishes the
   * already-rendered string rather than re-deriving the number, so there is
   * exactly one source of truth for what the shopper sees.
   */
  #broadcastSelectedPrice() {
    const selected = this.#getSelectedInput();
    if (!selected) return;

    const card = selected.closest('.quantity-tier__card');
    const price = card?.querySelector('.quantity-tier__price-sale')?.textContent?.trim();
    if (!price) return;

    // Only present when this tier actually shows a discount, so listeners can
    // hide their compare-at element when it comes through empty.
    const comparePrice = card?.querySelector('.quantity-tier__price-compare')?.textContent?.trim() ?? '';

    document.dispatchEvent(
      new CustomEvent('quantity-tier:price', {
        detail: {
          productId: this.dataset.productId,
          price,
          comparePrice,
          quantity: this.#getTierQuantity(selected),
        },
      })
    );
  }

  /**
   * Recomputes each tier's displayed price from the current variant price and
   * that tier's current quantity, mirroring the Liquid math in
   * blocks/quantity-tier.liquid exactly so the server-rendered and
   * client-rendered prices never disagree. The discount percentage itself is
   * still a merchant-entered value that must match the live Shopify
   * Automatic Discount - this only keeps the *base* price live.
   */
  #refreshDisplayedPrices() {
    const variantPrice = Number(this.dataset.variantPrice);
    if (!Number.isFinite(variantPrice)) return;
    const variantCompare = Number(this.dataset.variantComparePrice) || 0;

    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || '';

    for (const input of this.refs.tierInputs ?? []) {
      const card = input.closest('.quantity-tier__card');
      if (!card) continue;

      const quantity = this.#getTierQuantity(input);
      const showCompare = input.dataset.showCompare === 'true';
      const showSale = input.dataset.showSale === 'true';
      const showStrikethrough = input.dataset.showStrikethrough !== 'false';
      const hideCents = input.dataset.hideCents === 'true';
      const discountPercentage = Number(input.dataset.discountPercentage) || 0;

      // Mirrors blocks/quantity-tier.liquid exactly - the two must agree or the
      // price changes the moment a shopper touches the variant picker.
      const onSale = showSale && variantCompare > variantPrice;
      const compareBasis = onSale ? variantCompare : variantPrice;

      const linePrice = variantPrice * quantity;
      const compareLinePrice = compareBasis * quantity;

      let finalLinePrice = linePrice;
      if (showCompare && discountPercentage > 0) {
        // Math.floor matches Liquid's integer division, so cents never drift.
        finalLinePrice = linePrice - Math.floor((linePrice * discountPercentage) / 100);
      }

      const saleEl = card.querySelector('.quantity-tier__price-sale');
      const compareEl = card.querySelector('.quantity-tier__price-compare');

      if (saleEl) saleEl.textContent = formatTierPrice(finalLinePrice, format, currency, hideCents);

      if (compareEl) {
        const strike = showStrikethrough && compareLinePrice > finalLinePrice;
        compareEl.hidden = !strike;
        if (strike) {
          compareEl.textContent = formatTierPrice(compareLinePrice, format, currency, hideCents);
        }
        // Red only for a real markdown, so it never colours a plain bundle
        // discount's strikethrough.
        compareEl.classList.toggle('quantity-tier__price-compare--sale', onSale);
      }

      const saleBadge = card.querySelector('.quantity-tier__badge--sale');
      if (saleBadge) {
        saleBadge.hidden = !onSale;
        if (onSale) {
          const percent = Math.round(((variantCompare - variantPrice) / variantCompare) * 100);
          saleBadge.textContent = (input.dataset.saleBadgeText || '').replace('[percent]', String(percent));
        }
      }
    }
  }
}

if (!customElements.get('quantity-tier-selector-component')) {
  customElements.define('quantity-tier-selector-component', QuantityTierSelectorComponent);
}
